import { RELEASE_STAGE } from "app/utils/constants/releaseStages";

/**
 * Tutorial sidebar menus that fuse several intents into a single entry whose
 * intents share one tutorial video, keyed by the menu label in videosToRender.
 * - `category`: every intent of that intentList category is fused.
 * - `intents`: only these intent descriptions are fused (in this order),
 *   wherever they appear; the rest of their category is left untouched.
 */
export const FUSED_TUTORIAL_MENUS = [
  {
    label: "Bibliographic Data",
    category: "Bibliographic Data Extraction",
  },
  {
    label: "Correct or Update Applicant Name(1.46)",
    intents: [
      "Change of correspondence address (Application)",
      "Change of correspondence address (Patent)",
      "Correct or Update Applicant Name(1.46)",
    ],
  },
  {
    label: "RCE",
    intents: [
      "RCE Transmittal",
      "RCE Transmittal (EFS)",
    ],
  },
];

const toMenuEntry = (intent) => ({
  label: intent.description,
  releaseStage: intent.releaseStage,
});

/**
 * Builds the tutorial sidebar items from the intent list, applying the fused
 * menus. Regular categories keep their dropdown (or are flattened when they
 * have a single entry). A category-fused menu becomes a top-level item; an
 * intent-fused menu becomes one dropdown entry of the category it is found in.
 * Fused entries carry `subIntents`.
 *
 * @param {Object} intentList - Map of category name to intent(s).
 * @param {Array} fusedMenus - Fused menu config, defaults to FUSED_TUTORIAL_MENUS.
 * @returns {Array} Sidebar items in intentList order; a fused menu takes the
 *   position of the first intent it absorbs. Fused menus without any available
 *   intent are omitted.
 */
export const buildTutorialSidebarItems = (intentList, fusedMenus = FUSED_TUTORIAL_MENUS) => {
  if (!intentList || Object.keys(intentList).length === 0) return [];

  const menuByCategory = new Map();
  const menuByIntent = new Map();
  fusedMenus.forEach((menu) => {
    if (menu.category) menuByCategory.set(menu.category, menu);
    (menu.intents || []).forEach((description) => menuByIntent.set(description, menu));
  });

  const items = [];
  const fusedEntries = new Map();

  // Returns the fused menu's entry, and whether this call created it.
  const getFusedEntry = (menu) => {
    const isNew = !fusedEntries.has(menu.label);
    if (isNew) {
      fusedEntries.set(menu.label, { label: menu.label, releaseStage: RELEASE_STAGE.STABLE, subIntents: [] });
    }
    return [fusedEntries.get(menu.label), isNew];
  };

  Object.entries(intentList).forEach(([parentKey, children]) => {
    const normalizedChildren = Array.isArray(children) ? children : [children];

    if (menuByCategory.has(parentKey)) {
      const [entry, isNew] = getFusedEntry(menuByCategory.get(parentKey));
      entry.subIntents.push(...normalizedChildren.map(toMenuEntry));
      if (isNew) items.push({ ...entry, subMenus: [], subKey: "" });
      return;
    }

    const subMenus = [];
    normalizedChildren.forEach((child) => {
      const menu = menuByIntent.get(child.description);
      if (!menu) {
        subMenus.push(toMenuEntry(child));
        return;
      }
      const [entry, isNew] = getFusedEntry(menu);
      entry.subIntents.push(toMenuEntry(child));
      if (isNew) subMenus.push(entry);
    });
    // Drop a category only when all of its intents went to fused menus listed elsewhere.
    if (subMenus.length === 0 && normalizedChildren.length > 0) return;

    // A task with a single subtask is flattened into a top-level entry, so the
    // release stage (and fused sub-intents) are carried up from that lone child.
    if (subMenus.length === 1) {
      items.push({ ...subMenus[0], subMenus, subKey: "" });
      return;
    }
    items.push({ label: parentKey, releaseStage: RELEASE_STAGE.STABLE, subMenus, subKey: "" });
  });

  // Intent-based fused menus follow their configured order, not API order.
  fusedMenus.forEach((menu) => {
    const entry = fusedEntries.get(menu.label);
    if (!menu.intents || !entry) return;
    entry.subIntents.sort((a, b) => menu.intents.indexOf(a.label) - menu.intents.indexOf(b.label));
  });

  return items;
};

/**
 * Finds a sidebar entry by label, whether it is a top-level item or a dropdown entry.
 *
 * @param {Array} sidebarItems - Items as returned by buildTutorialSidebarItems.
 * @param {string} label - Selected sidebar label.
 * @returns {Object|undefined} The matching item or dropdown entry.
 */
export const findTutorialMenuEntry = (sidebarItems, label) =>
  sidebarItems
    .flatMap((item) => [item, ...(item.subMenus || [])])
    .find((entry) => entry.label === label);
